import {
  useRef,
  useState,
  type MutableRefObject,
  type Dispatch,
  type SetStateAction
} from 'react';
import type { AnyAction } from '@reduxjs/toolkit';
import { clearUnsavedFeaturesFromSessionStorage } from '../utils/unsavedFeaturesStorage';
import { homeActions } from '../../../pages/Home/slice';
import { layoutActions } from '../../../pages/Layout/slice';
import { pendingApprovalsActions } from '../../../store/pendingApprovalsSlice';

export type UseSlideoutCloseParams = {
  onClose: () => void;
  hasUnsavedChanges: () => boolean;
  overviewHasUnsavedChanges: MutableRefObject<boolean>;
  setOverviewResetTrigger: Dispatch<SetStateAction<number>>;
  isCommentPanelOpen: boolean;
  setIsCommentPanelOpen: (open: boolean) => void;
  setIsFullScreen: (value: boolean) => void;
  location: { pathname: string };
  dispatch: Dispatch<AnyAction>;
  alertsActions: { loadAlertsAndNotifications: () => AnyAction };
  setIsCloneModalOpen?: Dispatch<SetStateAction<boolean>>;
};

/**
 * Close animation, unsaved-changes warning, and silent roadmap refresh on close.
 * Extracted from SlideOutPanel/index.tsx (Part 2A modularisation).
 */
export function useSlideoutClose({
  onClose,
  hasUnsavedChanges,
  overviewHasUnsavedChanges,
  setOverviewResetTrigger,
  isCommentPanelOpen,
  setIsCommentPanelOpen,
  setIsFullScreen,
  location,
  dispatch,
  alertsActions,
  setIsCloneModalOpen
}: UseSlideoutCloseParams) {
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const pendingCloseAction = useRef<(() => void) | null>(null);
  const [showSlideAnimation, setShowSlideAnimation] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const prevCardId = useRef<string | null>(null);

  const performClose = () => {
    setIsClosing(true);
    setShowSlideAnimation(false);
    prevCardId.current = null;
    setIsFullScreen(false);

    if (isCommentPanelOpen) {
      setIsCommentPanelOpen(false);
    }

    dispatch(layoutActions.closeAiAssistant());
    setIsCloneModalOpen?.(false);

    clearUnsavedFeaturesFromSessionStorage();

    overviewHasUnsavedChanges.current = false;

    setOverviewResetTrigger(prev => prev + 1);

    if (!location.pathname.includes('/milestones')) {
      dispatch(homeActions.loadReleaseDataSilent({}));
    }

    dispatch(alertsActions.loadAlertsAndNotifications());
    // Review & Approval owns one four-resource refresh on the committed close.
    if (!location.pathname.startsWith('/review-approval')) {
      dispatch(pendingApprovalsActions.loadPendingApprovalsCount());
    }

    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setShowUnsavedWarning(false);
    }, 50);
  };

  const handleCloseWithRefresh = () => {
    if (isClosing) return;

    if (hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
      pendingCloseAction.current = () => performClose();
      return;
    }

    performClose();
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (pendingCloseAction.current) {
      pendingCloseAction.current();
      pendingCloseAction.current = null;
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
    pendingCloseAction.current = null;
  };

  return {
    showUnsavedWarning,
    setShowUnsavedWarning,
    pendingCloseAction,
    showSlideAnimation,
    setShowSlideAnimation,
    isClosing,
    setIsClosing,
    prevCardId,
    handleCloseWithRefresh,
    performClose,
    handleConfirmClose,
    handleCancelClose
  };
}
